import {MetricDefinition} from 'common/react-query/metrics/types';
import {FilterClause} from 'common/report/insights/util/types';
import type {Content} from 'common/types/app-api/content';

export const enum ExperimentStatus {
  DRAFT = `draft`,
  ACTIVE = `active`,
  CONCLUDED = `concluded`,
  SUCCESS = `success`,
  FAIL = `fail`,
}

export type ExperimentVariant = {
  id: string;
  name: string;
};

export const enum ExperimentMetricType {
  PRIMARY = `primary`,
  SECONDARY = `secondary`,
}

export type ExperimentMetric = {
  id: string;
  name: string;
  type: ExperimentMetricType;
  definition: MetricDefinition;
  dataGroupId?: string;
  filters?: FilterClause[];
};

export const enum ExperimentCollectionType {
  FEATURE_FLAG = `feature_flag`,
  EXPOSURE_EVENTS = `exposure_events`,
  USER_PROPERTIES = `user_properties`,
  EVENT_PROPERTIES = `event_properties`,
}

export const enum ExperimentTestingModel {
  FREQUENTIST = `frequentist`,
}

export type ExperimentFeatureFlagSettings = {
  experimentName: string;
};

export type ExperimentExposureEventSettings = {
  experimentName: string;
};

export type ExperimentUserPropertiesSettings = {
  propertyName: string;
};

export type ExperimentEventPropertiesSettings = {
  propertyName: string;
};

export type ExperimentSettings = {
  collectionMethod: ExperimentCollectionType;
  featureFlagSettings?: ExperimentFeatureFlagSettings;
  exposureEventSettings?: ExperimentExposureEventSettings;
  userPropertiesSettings?: ExperimentUserPropertiesSettings;
  eventPropertiesSettings?: ExperimentEventPropertiesSettings;
  sampleSize?: number;
  confidenceLevel?: number;
  testingModel?: ExperimentTestingModel;
  controlKey?: string;
};

export type ExperimentExposuresCache = {
  [variant: string]: number;
};

export type ExperimentMetricLifts = {
  [metricId: string]: number;
};

export type ExperimentVariantLifts = {
  [variantId: string]: ExperimentMetricLifts;
};

export type ExperimentResultsCache = {
  lifts: ExperimentVariantLifts;
  message?: string;
  messageUpdatedAt?: string;
  messageUpdatedBy?: string;
};

export type Experiment = Content & {
  description?: null | string;
  hypothesis?: null | string;
  status: ExperimentStatus;
  variants: ExperimentVariant[];
  metrics: ExperimentMetric[];
  settings: ExperimentSettings;
  exposures_cache: ExperimentExposuresCache;
  results_cache: ExperimentResultsCache;
  start_date?: string;
  end_date?: string;
};

export const enum StatisticallySignificant {
  YES_POSITIVE = `positive`,
  YES_NEGATIVE = `negative`,
  NO = `no`,
}

export type ExperimentMetricResultsFormat = `number` | `percent`;

export type ExperimentMetricResults = {
  control: number;
  controlFormat: ExperimentMetricResultsFormat;
  treatment: number;
  treatmentFormat: ExperimentMetricResultsFormat;
  lift: number;
  liftConfidence: number;
  pValue: number;
  statisticallySignificant: StatisticallySignificant;
};
